import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import TransactionDocuments from "./TransactionDocuments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios keeps the httpOnly agent_session cookie in flight for us.
axios.defaults.withCredentials = true;

// ---------------------------------------------------------------------------
// Root — auth gate + tab shell
// ---------------------------------------------------------------------------

export default function AgentPortal() {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard");

  useEffect(() => {
    axios.get(`${API}/agent/me`)
      .then((res) => { setAgent(res.data); setLoading(false); })
      .catch(() => { setAgent(false); setLoading(false); });
  }, []);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0F0F0F] text-[#C4A56B]">Loading…</div>;
  if (!agent) return <AgentLogin onLoggedIn={(a) => setAgent(a)} />;
  if (agent.mustChangePassword && view !== "change-password") return <ChangePasswordScreen agent={agent} onDone={() => { setAgent({ ...agent, mustChangePassword: false }); setView("dashboard"); }} />;

  return <AgentDashboard
    agent={agent}
    onLogout={() => { axios.post(`${API}/agent/logout`).finally(() => setAgent(false)); }}
    onChangePassword={() => setView("change-password")}
  />;
}

// ---------------------------------------------------------------------------
// Auth screens
// ---------------------------------------------------------------------------

function AgentLogin({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/agent/login`, { email, password });
      onLoggedIn(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F0F0F] p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-8 text-[#E0D2A8]" data-testid="agent-login-form">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C4A56B]">Agent Portal</div>
        <h1 className="mt-3 font-serif text-3xl">Sign in</h1>
        <p className="mt-2 text-sm text-[#E0D2A8]/70">Irving Real Estate Group · team members only</p>
        <div className="mt-6 space-y-3">
          <label className="block text-xs">
            <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Email</span>
            <input data-testid="agent-login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Password</span>
            <input data-testid="agent-login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
          </label>
          {error && <div className="rounded-lg bg-red-950/40 p-2 text-xs text-red-200">{error}</div>}
          <button data-testid="agent-login-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-[#C4A56B] px-6 py-3 font-bold text-[#1E1E1E] disabled:opacity-50">
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-center text-[11px] text-[#E0D2A8]/50">Forgot your password? Ask your admin to reset it.</p>
        </div>
      </form>
    </div>
  );
}

function ChangePasswordScreen({ agent, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) return setErr("Passwords don't match.");
    if (next.length < 8) return setErr("New password must be at least 8 characters.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/agent/change-password`, { currentPassword: current, newPassword: next });
      onDone();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Update failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0F0F0F] p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-8 text-[#E0D2A8]" data-testid="agent-change-pw-form">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C4A56B]">First-time sign-in</div>
        <h1 className="mt-3 font-serif text-2xl">Set a new password</h1>
        <p className="mt-2 text-sm text-[#E0D2A8]/70">Welcome {agent.name}. Choose a password only you know.</p>
        <div className="mt-6 space-y-3">
          <input required type="password" placeholder="Current (temp) password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
          <input required type="password" placeholder="New password (min 8 chars)" value={next} onChange={(e) => setNext(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
          <input required type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-4 py-3 text-sm text-[#E0D2A8]" />
          {err && <div className="rounded-lg bg-red-950/40 p-2 text-xs text-red-200">{err}</div>}
          <button data-testid="agent-change-pw-submit" type="submit" disabled={submitting} className="w-full rounded-full bg-[#C4A56B] px-6 py-3 font-bold text-[#1E1E1E]">
            {submitting ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard — 4 tabs: Leads / Pipeline / Transactions / Content Studio
// ---------------------------------------------------------------------------

const TABS = [
  { key: "leads",       label: "My Leads" },
  { key: "pipeline",    label: "Pipeline" },
  { key: "transactions", label: "Transactions" },
  { key: "content",     label: "Content Studio" },
];

function AgentDashboard({ agent, onLogout, onChangePassword }) {
  const [tab, setTab] = useState("leads");
  const [activeTxn, setActiveTxn] = useState(null);
  const [activeLead, setActiveLead] = useState(null);

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-[#E0D2A8]" data-testid="agent-dashboard">
      <header className="border-b border-[#C4A56B]/20 bg-[#141414]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-8">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Agent Portal · Irving Real Estate Group</div>
            <div className="font-serif text-lg">Welcome, {agent.name}</div>
          </div>
          <div className="flex gap-2 text-xs">
            <button data-testid="agent-change-pw" onClick={onChangePassword} className="rounded-full border border-[#C4A56B]/40 px-3 py-1.5 font-bold text-[#C4A56B]">Change password</button>
            <button data-testid="agent-logout" onClick={onLogout} className="rounded-full border border-[#C4A56B]/40 px-3 py-1.5 font-bold text-[#C4A56B]">Log out</button>
          </div>
        </div>
      </header>

      {activeTxn ? (
        <AgentTxnDetail
          agent={agent}
          txn={activeTxn}
          back={() => setActiveTxn(null)}
          refresh={(next) => setActiveTxn(next)}
        />
      ) : activeLead ? (
        <AgentLeadDetail
          lead={activeLead}
          onClose={() => setActiveLead(null)}
          onUpdated={(next) => setActiveLead(next)}
        />
      ) : (
        <main className="mx-auto max-w-6xl px-4 py-8 md:px-8">
          <div className="mb-6 flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`agent-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`rounded-full px-4 py-2 text-sm font-bold ${tab === t.key ? "bg-[#C4A56B] text-[#1E1E1E]" : "bg-[#141414] text-[#E0D2A8] hover:bg-[#1a1a1a]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "leads"       && <AgentLeadsTab agentId={agent.id} onOpenLead={setActiveLead} />}
          {tab === "pipeline"    && <AgentPipelineTab />}
          {tab === "transactions" && <AgentTxnsTab onOpenTxn={setActiveTxn} />}
          {tab === "content"     && <AgentContentStudio />}
        </main>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leads tab
// ---------------------------------------------------------------------------

function AgentLeadsTab({ onOpenLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/agent/leads`).then((r) => setLeads(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-[#E0D2A8]/60">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="agent-leads-list">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">My Leads ({leads.length})</div>
        <button
          data-testid="agent-new-lead-btn"
          onClick={() => setShowAdd(true)}
          className="rounded-full bg-[#C4A56B] px-4 py-2 text-xs font-bold text-[#1E1E1E] hover:bg-[#d4b876]"
        >
          + New Lead
        </button>
      </div>

      {leads.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#C4A56B]/30 bg-[#141414] p-8 text-center text-sm text-[#E0D2A8]/60">
          No leads yet. Tap "+ New Lead" to add one, or wait for a lead to be assigned to you.
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((l) => (
            <button
              key={l.id}
              data-testid={`agent-lead-card-${l.id}`}
              onClick={() => onOpenLead(l)}
              className="block w-full rounded-2xl border border-[#C4A56B]/20 bg-[#141414] p-4 text-left transition hover:border-[#C4A56B]/60"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-[#E0D2A8]">{l.name || "(no name)"}</div>
                  <div className="text-xs text-[#E0D2A8]/60">{l.email || "-"} · {l.phone || "-"}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#0F0F0F] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#E0D2A8]/70">{l.pipelineStage || "-"}</span>
                  <span className="rounded-full bg-[#C4A56B]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">{l.type} · {l.score || "-"}</span>
                </div>
              </div>
              {l.recommendedNextAction && (
                <div className="mt-2 rounded-lg bg-[#0F0F0F] px-3 py-2 text-xs text-[#E0D2A8]/80">
                  <strong className="text-[#C4A56B]">Next action:</strong> {l.recommendedNextAction}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {showAdd && (
        <AgentNewLeadModal
          onClose={() => setShowAdd(false)}
          onCreated={(lead) => { setLeads((p) => [lead, ...p]); setShowAdd(false); onOpenLead(lead); }}
        />
      )}
    </div>
  );
}

function AgentNewLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: "Buyer", name: "", email: "", phone: "",
    location: "", budget: "", timeline: "", preapprovalStatus: "",
    propertyAddress: "", homeCondition: "", reason: "", desiredPrice: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name && !form.email && !form.phone) {
      setErr("Provide at least a name, email, or phone.");
      return;
    }
    setSubmitting(true); setErr("");
    try {
      const res = await axios.post(`${API}/agent/leads`, form);
      onCreated(res.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Create failed. Try again.");
    } finally { setSubmitting(false); }
  };

  const isBuyer = form.type === "Buyer";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-6 text-[#E0D2A8]"
        data-testid="agent-new-lead-modal"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">New Lead</div>
            <h2 className="mt-1 font-serif text-xl">Add a client to your pipeline</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-[#C4A56B]/40 px-3 py-1 text-xs text-[#C4A56B]">Close</button>
        </div>

        <div className="mt-5 space-y-4">
          <div className="flex gap-2">
            {["Buyer", "Seller"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`agent-lead-type-${t.toLowerCase()}`}
                onClick={() => set("type", t)}
                className={`flex-1 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest ${form.type === t ? "bg-[#C4A56B] text-[#1E1E1E]" : "border border-[#C4A56B]/30 text-[#E0D2A8]/70 hover:border-[#C4A56B]/60"}`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Client name" testId="agent-lead-name" value={form.name} onChange={(v) => set("name", v)} placeholder="Sarah Watson" />
            <Field label="Phone" testId="agent-lead-phone" value={form.phone} onChange={(v) => set("phone", v)} placeholder="(817) 555-1234" />
            <Field label="Email" testId="agent-lead-email" value={form.email} onChange={(v) => set("email", v)} placeholder="sarah@example.com" />
            <Field label="Timeline" testId="agent-lead-timeline" value={form.timeline} onChange={(v) => set("timeline", v)} placeholder="0-90 days" />
          </div>

          {isBuyer ? (
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Location / City" testId="agent-lead-location" value={form.location} onChange={(v) => set("location", v)} placeholder="Frisco" />
              <Field label="Budget" testId="agent-lead-budget" value={form.budget} onChange={(v) => set("budget", v)} placeholder="$500-700k" />
              <Field label="Preapproval status" testId="agent-lead-preapproval" value={form.preapprovalStatus} onChange={(v) => set("preapprovalStatus", v)} placeholder="Preapproved / Working on it / None" />
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Property address" testId="agent-lead-property" value={form.propertyAddress} onChange={(v) => set("propertyAddress", v)} placeholder="1234 Preston Rd, Frisco TX" />
              <Field label="Desired price" testId="agent-lead-desired-price" value={form.desiredPrice} onChange={(v) => set("desiredPrice", v)} placeholder="$650,000" />
              <Field label="Home condition" testId="agent-lead-condition" value={form.homeCondition} onChange={(v) => set("homeCondition", v)} placeholder="Move-in ready / Needs work" />
              <Field label="Reason for selling" testId="agent-lead-reason" value={form.reason} onChange={(v) => set("reason", v)} placeholder="Relocating / Downsizing / Upsizing" />
            </div>
          )}

          <label className="block text-xs">
            <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Notes</span>
            <textarea data-testid="agent-lead-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm text-[#E0D2A8]" />
          </label>

          {err && <div data-testid="agent-lead-err" className="rounded-lg border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-200">{err}</div>}

          <button
            type="submit"
            data-testid="agent-lead-submit"
            disabled={submitting}
            className="w-full rounded-full bg-[#C4A56B] px-6 py-3 font-bold text-[#1E1E1E] disabled:opacity-50"
          >
            {submitting ? "Adding…" : "Add Lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testId, value, onChange, placeholder }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">{label}</span>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm text-[#E0D2A8]"
      />
    </label>
  );
}

function AgentLeadDetail({ lead, onClose, onUpdated }) {
  const [stages, setStages] = useState([]);
  const [pipelineStage, setPipelineStage] = useState(lead.pipelineStage || "");
  const [notes, setNotes] = useState(lead.notes || "");
  const [saveState, setSaveState] = useState("");

  useEffect(() => {
    axios.get(`${API}/leads/pipeline-stages`).then((r) => setStages(r.data?.stages || [])).catch(() => {});
  }, []);

  const saveStage = async (val) => {
    setPipelineStage(val);
    setSaveState("saving");
    try {
      const res = await axios.patch(`${API}/agent/leads/${lead.id}/stage`, { pipelineStage: val });
      onUpdated(res.data);
      setSaveState("saved");
      setTimeout(() => setSaveState(""), 1500);
    } catch { setSaveState("error"); }
  };

  const saveNotes = async () => {
    setSaveState("saving");
    try {
      await axios.patch(`${API}/agent/leads/${lead.id}/notes`, { notes });
      onUpdated({ ...lead, notes });
      setSaveState("saved");
      setTimeout(() => setSaveState(""), 1500);
    } catch { setSaveState("error"); }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-8 md:px-8" data-testid="agent-lead-detail">
      <button data-testid="agent-lead-back" onClick={onClose} className="mb-4 rounded-full border border-[#C4A56B]/40 px-4 py-2 text-xs font-bold text-[#C4A56B]">← Back to leads</button>
      <div className="rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Lead · {lead.type}</div>
            <h1 className="mt-1 font-serif text-2xl">{lead.name || "(no name)"}</h1>
            <div className="mt-1 text-xs text-[#E0D2A8]/70">{lead.email || "-"} · {lead.phone || "-"} · {lead.location || "-"}</div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="rounded-full bg-[#C4A56B]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">{lead.score || "-"}</span>
            <span className="text-[10px] text-[#E0D2A8]/50">Source: {lead.source || "-"}</span>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Info label="Timeline" value={lead.timeline} />
          <Info label="Budget" value={lead.budget} />
          <Info label="Preapproval" value={lead.preapprovalStatus} />
          <Info label="Must-haves" value={lead.mustHaves} />
        </div>

        {lead.recommendedNextAction && (
          <div className="mt-6 rounded-2xl border border-[#C4A56B]/40 bg-[#0F0F0F] p-4">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Recommended next action</div>
            <div className="mt-1 text-sm text-[#E0D2A8]">{lead.recommendedNextAction}</div>
          </div>
        )}

        {lead.conversationSummary && (
          <div className="mt-4 rounded-2xl border border-[#C4A56B]/20 bg-[#0F0F0F] p-4 text-sm text-[#E0D2A8]/80">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Jason's summary</div>
            <div className="mt-1 whitespace-pre-wrap">{lead.conversationSummary}</div>
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-[#C4A56B]/30 bg-[#0F0F0F] p-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Pipeline stage</div>
          <select
            data-testid="agent-lead-stage-select"
            value={pipelineStage}
            onChange={(e) => saveStage(e.target.value)}
            className="w-full rounded-xl border border-[#C4A56B]/30 bg-[#141414] px-3 py-2 text-sm text-[#E0D2A8]"
          >
            <option value="">(unset)</option>
            {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {saveState === "saved" && <div className="mt-1 text-[10px] text-green-400">Saved ✓</div>}
          {saveState === "error" && <div className="mt-1 text-[10px] text-red-400">Save failed. Try again.</div>}
        </div>

        <div className="mt-4 rounded-2xl border border-[#C4A56B]/30 bg-[#0F0F0F] p-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Notes</div>
          <textarea
            data-testid="agent-lead-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
            className="w-full rounded-xl border border-[#C4A56B]/30 bg-[#141414] px-3 py-2 text-sm text-[#E0D2A8]"
          />
          <button
            data-testid="agent-lead-notes-save"
            onClick={saveNotes}
            className="mt-2 rounded-full bg-[#C4A56B] px-4 py-1.5 text-xs font-bold text-[#1E1E1E]"
          >
            Save notes
          </button>
        </div>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Pipeline tab — Phase-grouped Kanban of the agent's own leads
// ---------------------------------------------------------------------------

const PHASE_TONE_DARK = {
  hot:       "bg-red-950/40 ring-red-500/30",
  warm:      "bg-orange-950/40 ring-orange-500/30",
  nurture:   "bg-emerald-950/40 ring-emerald-500/30",
  education: "bg-[#141414] ring-[#C4A56B]/20",
  incomplete:"bg-yellow-950/40 ring-yellow-500/30",
  action:    "bg-sky-950/40 ring-sky-500/30",
  closing:   "bg-indigo-950/40 ring-indigo-500/30",
  closed:    "bg-purple-950/40 ring-purple-500/30",
  past:      "bg-teal-950/40 ring-teal-500/30",
  lost:      "bg-[#0a0a0a] ring-slate-700",
};

function AgentPipelineTab() {
  const [phases, setPhases] = useState([]);
  const [stages, setStages] = useState([]);
  const [byStage, setByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/agent/pipeline`).then((r) => {
      setStages(r.data?.stages || []);
      setPhases(r.data?.phases || []);
      setByStage(r.data?.byStage || {});
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, leadId) => e.dataTransfer.setData("text/plain", leadId);
  const onDrop = async (e, stageKey) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain");
    if (!leadId) return;
    setByStage((prev) => {
      const next = { ...prev };
      let moved = null;
      for (const k of Object.keys(next)) {
        const idx = next[k].findIndex((l) => l.id === leadId);
        if (idx >= 0) { moved = next[k][idx]; next[k] = [...next[k].slice(0, idx), ...next[k].slice(idx + 1)]; break; }
      }
      if (moved) {
        moved = { ...moved, pipelineStage: stageKey };
        next[stageKey] = [moved, ...(next[stageKey] || [])];
      }
      return next;
    });
    try { await axios.patch(`${API}/agent/leads/${leadId}/stage`, { pipelineStage: stageKey }); }
    catch { load(); }
  };

  if (loading) return <div className="text-sm text-[#E0D2A8]/60">Loading pipeline…</div>;

  const total = Object.values(byStage).reduce((s, arr) => s + (arr?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="agent-pipeline">
      <div className="rounded-2xl border border-[#C4A56B]/30 bg-[#141414] p-4 text-[#E0D2A8]">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Pipeline</div>
        <div className="mt-1 text-lg font-bold">{total} lead{total === 1 ? "" : "s"} across {phases.length} phases</div>
        <div className="mt-1 text-xs text-[#E0D2A8]/60">Click a phase to expand its stages. Drag any card between stages.</div>
      </div>

      {/* Phase grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {phases.map((phase) => {
          const count = phase.stages.reduce((s, k) => s + (byStage[k]?.length || 0), 0);
          const isOpen = expandedPhase === phase.key;
          return (
            <button
              key={phase.key}
              data-testid={`agent-pipeline-phase-${phase.key}`}
              onClick={() => setExpandedPhase(isOpen ? null : phase.key)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, phase.stages[0])}
              className={`rounded-2xl p-4 text-left ring-1 transition ${PHASE_TONE_DARK[phase.tone] || "bg-[#141414] ring-[#C4A56B]/20"} ${isOpen ? "ring-2 ring-[#C4A56B]" : "hover:ring-[#C4A56B]/60"}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-xs font-bold uppercase tracking-widest text-[#E0D2A8]">{phase.emoji} {phase.label}</div>
                <div className="rounded-full bg-[#0F0F0F] px-2 py-0.5 text-xs font-bold text-[#C4A56B]">{count}</div>
              </div>
              <div className="mt-1 text-[10px] text-[#E0D2A8]/60">{phase.stages.length} stage{phase.stages.length === 1 ? "" : "s"} · tap to {isOpen ? "collapse" : "expand"}</div>
            </button>
          );
        })}
      </div>

      {/* Expanded stage board */}
      {expandedPhase && (() => {
        const phase = phases.find((p) => p.key === expandedPhase);
        if (!phase) return null;
        const stageDefs = stages.filter((s) => phase.stages.includes(s.key));
        stageDefs.sort((a, b) => phase.stages.indexOf(a.key) - phase.stages.indexOf(b.key));
        const cols = { 1: "grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4", 5: "sm:grid-cols-2 lg:grid-cols-5", 6: "sm:grid-cols-2 lg:grid-cols-6" }[Math.min(stageDefs.length, 6)] || "sm:grid-cols-2 lg:grid-cols-4";
        return (
          <div className={`grid gap-3 ${cols}`} data-testid={`agent-phase-expanded-${phase.key}`}>
            {stageDefs.map((s) => (
              <div
                key={s.key}
                data-testid={`agent-pipeline-col-${s.key}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, s.key)}
                className={`rounded-2xl p-3 ring-1 ${PHASE_TONE_DARK[s.tone] || "bg-[#141414] ring-[#C4A56B]/20"}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-[#E0D2A8]">{s.label}</div>
                  <span className="rounded-full bg-[#0F0F0F] px-2 py-0.5 text-[10px] text-[#E0D2A8]/70">{(byStage[s.key] || []).length}</span>
                </div>
                <div className="space-y-2">
                  {(byStage[s.key] || []).map((l) => (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, l.id)}
                      className="cursor-grab rounded-xl border border-[#C4A56B]/20 bg-[#0F0F0F] p-3 text-sm active:cursor-grabbing"
                      data-testid={`agent-pipeline-card-${l.id}`}
                    >
                      <div className="font-bold text-[#E0D2A8]">{l.name || "(no name)"}</div>
                      <div className="mt-0.5 text-[10px] text-[#E0D2A8]/60">{l.type} · {l.score || "-"}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transactions tab
// ---------------------------------------------------------------------------

function AgentTxnsTab({ onOpenTxn }) {
  const [txns, setTxns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/agent/transactions`),
      axios.get(`${API}/agent/leads`),
    ]).then(([t, l]) => {
      setTxns(t.data || []);
      setLeads(l.data || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-[#E0D2A8]/60">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="agent-txns-list">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">My Active Transactions ({txns.length})</div>
        <button
          data-testid="agent-new-txn-btn"
          onClick={() => setShowModal(true)}
          className="rounded-full bg-[#C4A56B] px-4 py-2 text-xs font-bold text-[#1E1E1E] hover:bg-[#d4b876]"
        >
          + New Transaction
        </button>
      </div>

      {txns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#C4A56B]/30 bg-[#141414] p-8 text-center text-sm text-[#E0D2A8]/60">
          No transactions yet. Tap "+ New Transaction" to convert one of your assigned leads into an active deal.
        </div>
      ) : (
        <div className="space-y-2">
          {txns.map((t) => (
            <button
              key={t.id}
              data-testid={`agent-txn-card-${t.id}`}
              onClick={() => onOpenTxn(t)}
              className="block w-full rounded-2xl border border-[#C4A56B]/20 bg-[#141414] p-4 text-left transition hover:border-[#C4A56B]/60"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-[#E0D2A8]">{t.propertyAddress}</div>
                  <div className="text-xs text-[#E0D2A8]/60">{t.clientName || "(no client)"} · {t.clientType}</div>
                </div>
                <span className="rounded-full bg-[#C4A56B] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#1E1E1E]">Stage {t.stage}/8 · {t.stageName}</span>
              </div>
              <div className="mt-2 text-xs text-[#E0D2A8]/70">Contract: {t.contractDate || "-"} · Target close: {t.targetCloseDate || "TBD"}</div>
            </button>
          ))}
        </div>
      )}

      {showModal && (
        <AgentNewTxnModal
          leads={leads}
          onClose={() => setShowModal(false)}
          onCreated={(txn) => { setTxns((prev) => [txn, ...prev]); setShowModal(false); onOpenTxn(txn); }}
        />
      )}
    </div>
  );
}

function AgentNewTxnModal({ leads, onClose, onCreated }) {
  const [leadId, setLeadId] = useState("");
  const [address, setAddress] = useState("");
  const [listPrice, setListPrice] = useState("");
  const [contractDate, setContractDate] = useState(new Date().toISOString().slice(0, 10));
  const [targetCloseDate, setTargetCloseDate] = useState("");
  const [optionPeriodEndDate, setOptionPeriodEndDate] = useState("");
  const [lenderName, setLenderName] = useState("");
  const [titleCompany, setTitleCompany] = useState("");
  const [earnestMoney, setEarnestMoney] = useState("");
  const [hoaName, setHoaName] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Only allow leads not already tied to a transaction.
  const eligible = leads.filter((l) => !l.transactionId);

  const submit = async (e) => {
    e.preventDefault();
    if (!leadId || !address) return;
    setSubmitting(true); setErr("");
    try {
      const res = await axios.post(`${API}/agent/transactions`, {
        leadId,
        propertyAddress: address,
        listPrice: Number(listPrice) || 0,
        contractDate,
        targetCloseDate,
        optionPeriodEndDate,
        lenderName,
        titleCompany,
        earnestMoney: Number(earnestMoney) || 0,
        hoaName,
        notes,
      });
      onCreated(res.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Create failed. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-6 text-[#E0D2A8]"
        data-testid="agent-new-txn-modal"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">New Transaction</div>
            <h2 className="mt-1 font-serif text-xl">Convert an assigned lead into a live deal</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full border border-[#C4A56B]/40 px-3 py-1 text-xs text-[#C4A56B]">Close</button>
        </div>

        <div className="mt-5 space-y-4">
          <label className="block text-xs">
            <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Client (assigned lead) *</span>
            <select
              data-testid="agent-txn-lead-select"
              required
              value={leadId}
              onChange={(e) => {
                setLeadId(e.target.value);
                const lead = leads.find((l) => l.id === e.target.value);
                if (lead?.propertyAddress && !address) setAddress(lead.propertyAddress);
              }}
              className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm text-[#E0D2A8]"
            >
              <option value="">Select a lead…</option>
              {eligible.length === 0 && <option disabled>No eligible leads (all have transactions)</option>}
              {eligible.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name || "(no name)"} · {l.type} · {l.email || l.phone || "-"}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs">
            <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Property address *</span>
            <input data-testid="agent-txn-address" required value={address} onChange={(e) => setAddress(e.target.value)} placeholder="1234 Preston Rd, Frisco TX 75033" className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
          </label>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">List / Sale price</span>
              <input data-testid="agent-txn-price" type="number" value={listPrice} onChange={(e) => setListPrice(e.target.value)} placeholder="650000" className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Earnest money</span>
              <input data-testid="agent-txn-earnest" type="number" value={earnestMoney} onChange={(e) => setEarnestMoney(e.target.value)} placeholder="5000" className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Contract date *</span>
              <input data-testid="agent-txn-contract-date" required type="date" value={contractDate} onChange={(e) => setContractDate(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Option period ends</span>
              <input data-testid="agent-txn-option-end" type="date" value={optionPeriodEndDate} onChange={(e) => setOptionPeriodEndDate(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Target close date</span>
              <input data-testid="agent-txn-close-date" type="date" value={targetCloseDate} onChange={(e) => setTargetCloseDate(e.target.value)} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">HOA (if any)</span>
              <input data-testid="agent-txn-hoa" value={hoaName} onChange={(e) => setHoaName(e.target.value)} placeholder="Preston Vineyards HOA" className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Lender</span>
              <input data-testid="agent-txn-lender" value={lenderName} onChange={(e) => setLenderName(e.target.value)} placeholder="Guild Mortgage" className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Title company</span>
              <input data-testid="agent-txn-title" value={titleCompany} onChange={(e) => setTitleCompany(e.target.value)} placeholder="Republic Title" className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
            </label>
          </div>

          <label className="block text-xs">
            <span className="mb-1 block font-bold uppercase tracking-wide text-[#C4A56B]">Notes</span>
            <textarea data-testid="agent-txn-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-xl border border-[#C4A56B]/25 bg-[#0F0F0F] px-3 py-2 text-sm" />
          </label>

          {err && <div data-testid="agent-txn-err" className="rounded-lg border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-200">{err}</div>}

          <button
            type="submit"
            data-testid="agent-txn-submit"
            disabled={submitting || !leadId || !address}
            className="w-full rounded-full bg-[#C4A56B] px-6 py-3 font-bold text-[#1E1E1E] disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create Transaction"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AgentTxnDetail({ agent, txn, back, refresh }) {
  const [current, setCurrent] = useState(txn);
  const advance = async () => {
    try {
      const res = await axios.post(`${API}/agent/transactions/${current.id}/advance`);
      setCurrent(res.data);
      refresh(res.data);
    } catch (e) { alert(e?.response?.data?.detail || "Advance failed"); }
  };
  const toggleCheck = async (key, done) => {
    try {
      const res = await axios.post(`${API}/transactions/${current.id}/check`, { itemKey: key, done });
      setCurrent(res.data);
    } catch (e) { alert(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 md:px-8" data-testid="agent-txn-detail">
      <button data-testid="agent-txn-back" onClick={back} className="mb-4 rounded-full border border-[#C4A56B]/40 px-4 py-2 text-xs font-bold text-[#C4A56B]">← Back to transactions</button>
      <div className="rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Active Transaction</div>
            <h1 className="mt-1 font-serif text-2xl">{current.propertyAddress}</h1>
            <p className="mt-1 text-sm text-[#E0D2A8]/70">Stage {current.stage} of 8 · {current.stageName}</p>
            {current.atRisk && (
              <div className="mt-3 rounded-xl border border-red-500/50 bg-red-950/40 p-3 text-sm text-red-200">
                <strong>⚠ At risk:</strong> {current.atRiskReason}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {current.stage < 8 && (
              <button data-testid="agent-txn-advance" onClick={advance} className="rounded-full bg-[#C4A56B] px-4 py-2 text-xs font-bold text-[#1E1E1E]">Advance to next stage</button>
            )}
            {current.stage === 8 && current.approvalStatus === "pending_approval" && (
              <div className="rounded-full border border-amber-500/50 bg-amber-950/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-200">Awaiting closing approval</div>
            )}
            {current.approvalStatus === "approved" && (
              <div className="rounded-full border border-emerald-500/50 bg-emerald-950/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-200">✓ Closing approved</div>
            )}
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-4">
          <Info label="Contract" value={current.contractDate} />
          <Info label="Target Close" value={current.targetCloseDate} />
          <Info label="Lender" value={current.lenderName} />
          <Info label="Title" value={current.titleCompany} />
        </div>

        <div className="mt-6 rounded-2xl border border-[#C4A56B]/20 bg-[#0F0F0F] p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Checklist</div>
          <ul className="mt-3 space-y-2">
            {(current.checklist || []).map((item) => (
              <li key={item.key} className="flex items-start gap-3 rounded-xl bg-[#141414] p-3">
                <input data-testid={`agent-txn-check-${item.key}`} type="checkbox" checked={!!item.done} onChange={(e) => toggleCheck(item.key, e.target.checked)} className="mt-1 h-4 w-4 accent-[#C4A56B]" />
                <span className={`text-sm ${item.done ? "text-[#E0D2A8]/40 line-through" : "text-[#E0D2A8]"}`}>
                  <span className="mr-2 rounded bg-[#C4A56B]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#C4A56B]">S{item.stage}</span>
                  {item.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6">
          <TransactionDocuments txnId={current.id} theme="dark" currentActorId={agent.id} />
        </div>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Content Studio (shared endpoints, agent-scoped saves)
// ---------------------------------------------------------------------------

function AgentContentStudio() {
  const [topics, setTopics] = useState([]);
  const [todays, setTodays] = useState("");
  const [topic, setTopic] = useState("");
  const [city, setCity] = useState("");
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hooks, setHooks] = useState([]);
  const [saved, setSaved] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/content/topics`).then((r) => {
      setTopics(r.data?.topics || []);
      setTodays(r.data?.todaysTopic || "");
      setTopic(r.data?.todaysTopic || "");
    });
    axios.get(`${API}/content/hooks`).then((r) => setHooks(r.data?.hooks || []));
    axios.get(`${API}/content/saved`).then((r) => setSaved(r.data || []));
  }, []);

  const generate = async () => {
    if (!topic) return;
    setLoading(true); setErr(""); setScript(null);
    try {
      const res = await axios.post(`${API}/content/generate`, { topic, city });
      setScript(res.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Generation failed."); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!script) return;
    try {
      const res = await axios.post(`${API}/content/save`, { ...script, topic, city });
      setSaved((prev) => [res.data, ...prev]);
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed."); }
  };

  const delSaved = async (id) => {
    try {
      await axios.delete(`${API}/content/saved/${id}`);
      setSaved((prev) => prev.filter((s) => s.id !== id));
    } catch (e) { setErr(e?.response?.data?.detail || "Delete failed."); }
  };

  const copy = (text) => navigator.clipboard?.writeText(text || "");

  return (
    <div className="space-y-6" data-testid="agent-content-studio">
      <div className="rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Daily Video Script</div>
        <div className="mt-1 text-sm text-[#E0D2A8]/70">Generate a Reels / TikTok / Shorts script in seconds. Data-driven when it needs to be.</div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {topics.map((t) => (
            <button
              key={t.key}
              data-testid={`agent-content-topic-${t.key}`}
              onClick={() => setTopic(t.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold ${topic === t.key ? "bg-[#C4A56B] text-[#1E1E1E]" : "bg-[#0F0F0F] text-[#E0D2A8] hover:bg-[#1a1a1a]"}`}
            >
              {t.label}{t.key === todays ? " · Today" : ""}
            </button>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid="agent-content-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City (optional)" className="flex-1 min-w-[160px] rounded-xl border border-[#C4A56B]/30 bg-[#0F0F0F] px-3 py-2 text-sm text-[#E0D2A8]" />
          <button data-testid="agent-content-generate" onClick={generate} disabled={loading} className="rounded-full bg-[#C4A56B] px-4 py-2 text-xs font-bold text-[#1E1E1E] disabled:opacity-50">
            {loading ? "Generating…" : "Generate"}
          </button>
        </div>

        {err && <div className="mt-3 rounded-lg bg-red-950/40 p-2 text-xs text-red-200">{err}</div>}

        {script && (
          <div className="mt-5 space-y-3 rounded-2xl border border-[#C4A56B]/20 bg-[#0F0F0F] p-4" data-testid="agent-content-result">
            <ScriptField label="Hook" text={script.hook} onCopy={() => copy(script.hook)} highlight />
            <ScriptField label="Script" text={script.script} onCopy={() => copy(script.script)} multiline />
            <ScriptField label="CTA" text={script.cta} onCopy={() => copy(script.cta)} />
            <ScriptField label="Caption" text={script.caption} onCopy={() => copy(script.caption)} multiline />
            {(script.hashtags || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">Hashtags</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(script.hashtags || []).map((h) => <span key={h} className="rounded-full bg-[#C4A56B]/15 px-2 py-0.5 text-[11px] text-[#C4A56B]">{h}</span>)}
                </div>
                <button onClick={() => copy((script.hashtags || []).join(" "))} className="mt-1 text-[10px] text-[#C4A56B] underline">Copy all</button>
              </div>
            )}
            {(script.brollShots || []).length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">B-roll ideas</div>
                <ul className="mt-1 list-disc pl-5 text-sm text-[#E0D2A8]/80">
                  {(script.brollShots || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
            <button data-testid="agent-content-save" onClick={save} className="rounded-full border border-[#C4A56B]/50 px-4 py-1.5 text-xs font-bold text-[#C4A56B]">Save to my library</button>
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">Viral Hook Library</div>
        <div className="mt-1 text-sm text-[#E0D2A8]/70">Tap to copy. Drop into any post.</div>
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {hooks.map((h, i) => (
            <button key={i} onClick={() => copy(h)} className="rounded-xl border border-[#C4A56B]/15 bg-[#0F0F0F] px-3 py-2 text-left text-xs text-[#E0D2A8] hover:border-[#C4A56B]/40">
              {h}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-[#C4A56B]/30 bg-[#141414] p-5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C4A56B]">My Saved Scripts ({saved.length})</div>
        {saved.length === 0
          ? <div className="mt-2 text-xs text-[#E0D2A8]/60">Save any generated script to keep it handy.</div>
          : (
            <ul className="mt-3 space-y-2">
              {saved.map((s) => (
                <li key={s.id} className="rounded-xl bg-[#0F0F0F] p-3 text-xs text-[#E0D2A8]" data-testid={`agent-content-saved-${s.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-bold">{s.hook || s.topic || "Saved script"}</div>
                      <div className="mt-0.5 text-[10px] text-[#E0D2A8]/50">{s.topic || "-"} · {s.city || "any city"} · {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : ""}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => copy(s.script)} className="rounded-full border border-[#C4A56B]/40 px-2 py-0.5 text-[10px] text-[#C4A56B]">Copy script</button>
                      <button onClick={() => delSaved(s.id)} className="rounded-full border border-red-400/40 px-2 py-0.5 text-[10px] text-red-300">Delete</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-[#C4A56B]/20 bg-[#0F0F0F] p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">{label}</div>
      <div className="mt-1 text-sm text-[#E0D2A8]">{value || "-"}</div>
    </div>
  );
}

function ScriptField({ label, text, onCopy, multiline, highlight }) {
  return (
    <div className={highlight ? "rounded-xl border border-[#C4A56B]/50 bg-[#141414] p-3" : ""}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#C4A56B]">{label}</div>
        <button onClick={onCopy} className="text-[10px] text-[#C4A56B] underline">Copy</button>
      </div>
      <div className={`mt-1 text-sm text-[#E0D2A8]/90 ${multiline ? "whitespace-pre-wrap" : ""}`}>{text || "-"}</div>
    </div>
  );
}
